/**
 * Most / least correlated shortlists (F15, plan §3.3).
 *
 * One descending scan serves both ends — the demo reverses the same sorted
 * array rather than sorting twice, and the reversal is why `least` is ascending
 * by ρ rather than "descending by -ρ", which would order ties the other way.
 *
 * Ranking lives next to `corr` (`rankByCorrelation`); this module only slices
 * the shortlists so diversification groups can share the same scan without
 * depending on a presentation shortlist module.
 */
import type { DiversificationMember } from '@meridian/contracts';

import { rankByCorrelation, type FactorSecurity, type RankedCorrelation } from './correlation';

export interface CorrelationExtremes {
  /** Highest ρ first. */
  readonly most: readonly DiversificationMember[];
  /** Lowest ρ first. */
  readonly least: readonly DiversificationMember[];
}

function strip(m: RankedCorrelation): DiversificationMember {
  return { ticker: m.ticker, rho: m.rho };
}

/** Slice most/least from an already-ranked list (shared scan with groups). */
export function correlationExtremesFromRanked(
  ranked: readonly RankedCorrelation[],
  k: number,
): CorrelationExtremes {
  if (k <= 0) return { most: [], least: [] };
  /* most and least must be DISJOINT. slice(0,k) and slice(-k) overlap once
     2k > length, and at k >= length they are the same set — a panel pair
     showing one list and its reverse. Halving is the honest answer for a
     small candidate set: it shrinks both shortlists rather than duplicating
     rows across them. */
  const half = Math.min(k, Math.floor(ranked.length / 2));
  if (half === 0) return { most: [], least: [] };
  return {
    most: ranked.slice(0, half).map(strip),
    /* Bottom-half without copying/reversing the full array. */
    least: ranked.slice(-half).reverse().map(strip),
  };
}

export function correlationExtremes(
  subject: FactorSecurity,
  candidates: readonly FactorSecurity[],
  k: number,
): CorrelationExtremes {
  return correlationExtremesFromRanked(rankByCorrelation(subject, candidates), k);
}
